"use client";

import { useState } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { Menu } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
  SheetTitle,
} from "@/components/ui/sheet";

const sections = [
  {
    title: "Getting Started",
    items: [
      { name: "Overview", href: "/docs" },
      { name: "Getting Started", href: "/docs/getting-started" },
      { name: "Migrating from Codemagic", href: "/docs/migrating-from-codemagic" },
    ],
  },
  {
    title: "Building",
    items: [
      { name: "Applications", href: "/docs/applications" },
      { name: "buildmagic.yaml", href: "/docs/workflows" },
      { name: "Environment Vars", href: "/docs/environment-variables" },
    ],
  },
  {
    title: "Signing & Publishing",
    items: [{ name: "Code Signing", href: "/docs/code-signing" }],
  },
  {
    title: "Integrations",
    items: [{ name: "GitHub & GitLab", href: "/docs/integrations" }],
  },
  {
    title: "Platform",
    items: [
      { name: "Teams", href: "/docs/teams" },
      { name: "Workers", href: "/docs/workers" },
      { name: "Billing", href: "/docs/billing" },
    ],
  },
  {
    title: "Reference",
    items: [{ name: "API Reference", href: "/docs/api-reference" }],
  },
];

function SidebarNav() {
  const pathname = usePathname();

  return (
    <nav className="space-y-6 px-3 py-4">
      {sections.map((section) => (
        <div key={section.title}>
          <h4 className="mb-1.5 px-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground/60">
            {section.title}
          </h4>
          <div className="space-y-0.5">
            {section.items.map((item) => {
              const isActive = pathname === item.href;
              return (
                <Link
                  key={item.href}
                  href={item.href}
                  className={cn(
                    "block rounded-lg px-3 py-1.5 text-sm font-medium transition-colors",
                    isActive
                      ? "bg-violet-500/10 text-violet-400"
                      : "text-muted-foreground hover:bg-accent/50 hover:text-foreground"
                  )}
                >
                  {item.name}
                </Link>
              );
            })}
          </div>
        </div>
      ))}
    </nav>
  );
}

export function DocsSidebar() {
  const [open, setOpen] = useState(false);

  return (
    <>
      {/* Desktop */}
      <aside className="hidden w-64 shrink-0 border-r border-border md:block">
        <div className="sticky top-14 h-[calc(100vh-3.5rem)] overflow-y-auto">
          <SidebarNav />
        </div>
      </aside>

      {/* Mobile */}
      <div className="fixed top-0 left-0 z-40 flex h-14 items-center px-4 md:hidden">
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild>
            <Button variant="ghost" size="icon">
              <Menu className="size-5" />
              <span className="sr-only">Open docs navigation</span>
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="w-64 p-0" showCloseButton={false}>
            <SheetTitle className="sr-only">Documentation Navigation</SheetTitle>
            <div className="h-full overflow-y-auto">
              <SidebarNav />
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </>
  );
}
